import { ref, computed } from 'vue';
import { MOBILE_VIBRATION_MS, BREAKPOINTS, POPUP_AUTO_DISMISS_MS } from '@/common/config';

export type AlertPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface AlertOptions {
  id?: string;
  message: string;
  classes?: string | string[];
  timeout?: number | null; // null = persistent
  position?: AlertPosition;
  vibrate?: boolean;
}

export interface Alert extends Required<AlertOptions> {
  id: string;
}

const alerts = ref<Alert[]>([]);

const isMobile = () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${BREAKPOINTS.mobile}px)`).matches;

export function useAlerts() {
  function add(options: AlertOptions) {
    const id = options.id ?? crypto.randomUUID();

    const alert: Alert = {
      id,
      message: options.message,
      classes: options.classes ?? '',
      timeout: options.timeout !== undefined ? options.timeout : POPUP_AUTO_DISMISS_MS,
      position: options.position ?? 'top-center',
      vibrate: options.vibrate ?? true,
    };

    alerts.value.push(alert);

    // Mobile vibration
    if (alert.vibrate && isMobile() && 'vibrate' in navigator) {
      try {
        navigator.vibrate(MOBILE_VIBRATION_MS);
      } catch {
        // Silently ignore - vibration blocked until user interaction
      }
    }

    // Auto dismiss
    if (alert.timeout !== null) {
      setTimeout(() => dismissAlert(id), alert.timeout);
    }

    return id;
  }

  function dismissAlert(id: string) {
    alerts.value = alerts.value.filter((a) => a.id !== id);
  }

  const groupedAlerts = computed(() => {
    const groups: Record<string, Alert[]> = {};

    for (const alert of alerts.value) {
      const position = isMobile() ? 'top-center' : alert.position;

      if (!groups[position]) groups[position] = [];
      groups[position].push(alert);
    }

    return groups;
  });

  return {
    alerts,
    groupedAlerts,
    add,
    dismissAlert,
  };
}

export default useAlerts;
